import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  setSelectedAgentSiteCode,
  setSelectedAgentName,
  setSelectedAgentMode,
} from '../../slices/AppsSlice';
import { toast } from 'react-toastify';
import useApiCall from '../../hooks/useApiCall';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SecurityIcon from '@mui/icons-material/Security';
import SchoolIcon from '@mui/icons-material/School';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HeadsetIcon from '@mui/icons-material/Headset';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VerifiedIcon from '@mui/icons-material/TaskAlt';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';

import { SLATE, INDIGO } from '../../constants/Colors';

// ─── Agent type definitions ────────────────────────────────────────────────────
const AGENT_TYPES = [
  {
    id: 'enrollment',
    name: 'Enrollment AI',
    badge: { label: 'High Demand', color: INDIGO[600], bg: INDIGO[50] },
    description:
      'Turn inquiries into enrollments with automated guidance across the full admissions journey.',
    stats: [
      { Icon: TrendingUpIcon, color: '#10b981', label: '+15% Enrollment Rate' },
      { Icon: AccessTimeIcon, color: '#06b6d4', label: 'Saves 40hrs/week Admin' },
    ],
    iconBg: INDIGO[600],
    AgentIcon: RocketLaunchIcon,
    available: true,
    verified: true,
  },
  {
    id: 'student_success',
    name: 'Student Success AI',
    badge: { label: 'Canvas Ready', color: '#7c3aed', bg: '#f5f3ff' },
    description:
      'Empower enrolled students with personalized guidance, proactive outreach, and academic support to help them stay on track and complete their degrees.',
    stats: [
      { Icon: TrendingUpIcon, color: '#10b981', label: 'Reduces Dropout by 8%' },
      { Icon: BoltIcon, color: '#f59e0b', label: 'Proactive Alerts' },
    ],
    iconBg: '#7c3aed',
    AgentIcon: SecurityIcon,
    available: true,
    verified: true,
  },
  {
    id: 'customer_support',
    name: 'Customer Support',
    badge: { label: 'Fast Response', color: '#2563eb', bg: '#eff6ff' },
    description:
      'Handle customer inquiries, order tracking, returns, and post-purchase support automatically.',
    stats: [
      { Icon: AccessTimeIcon, color: '#06b6d4', label: '30sec Avg Response' },
      { Icon: CheckCircleOutlineIcon, color: '#10b981', label: '85% Self-Service Rate' },
    ],
    iconBg: '#3b82f6',
    AgentIcon: HeadsetIcon,
    available: true,
    verified: true,
  },
  {
    id: 'program_recommendation',
    name: 'Program Recommendation',
    badge: { label: 'LLM Powered', color: '#059669', bg: '#ecfdf5' },
    description:
      'Guide students through complex course catalogs and degree requirements with personalized recommendations.',
    stats: [
      { Icon: AccessTimeIcon, color: '#06b6d4', label: 'Saves 20h/week/advisor' },
      { Icon: CheckCircleOutlineIcon, color: '#10b981', label: '92% Student Satisfaction' },
    ],
    iconBg: '#059669',
    AgentIcon: SchoolIcon,
    available: false,
    verified: false,
  },
  // NOTE: the cards below are `available: false` and their ids are not yet
  // values in the agent_type enum (configurator-api/app/models/agent.rb).
  // Add the value there before setting `available: true` on any of them.
  {
    id: 'lead_scoring',
    name: 'Lead Scoring',
    badge: { label: 'AI-Powered', color: INDIGO[600], bg: INDIGO[50] },
    description:
      'Automatically qualify and score leads based on conversation signals and engagement patterns.',
    stats: [
      { Icon: TrendingUpIcon, color: '#10b981', label: '+40% Qualified Leads' },
      { Icon: BoltIcon, color: '#f59e0b', label: 'Real-time Scoring' },
    ],
    iconBg: INDIGO[600],
    AgentIcon: TrackChangesIcon,
    available: false,
    verified: false,
  },
  {
    id: 'risk_communication_ai',
    name: 'Risk & Communication AI',
    badge: { label: 'Real-time', color: '#dc2626', bg: '#fef2f2' },
    description:
      'Analyze campus data feeds to detect anomalies and potential safety risks before they escalate.',
    stats: [
      { Icon: CheckCircleOutlineIcon, color: '#10b981', label: '30% Faster Response' },
      { Icon: LocationOnIcon, color: '#6366f1', label: 'Geo-fencing Ready' },
    ],
    iconBg: '#ef4444',
    AgentIcon: NotificationsActiveIcon,
    available: false,
    verified: false,
  },
  {
    id: 'ecommerce_sales_ai',
    name: 'Ecommerce Sales AI',
    badge: { label: 'High Conversion', color: '#d97706', bg: '#fffbeb' },
    description:
      'Qualify leads, answer product questions, and guide customers through the purchase funnel.',
    stats: [
      { Icon: TrendingUpIcon, color: '#10b981', label: '+25% Conversion Rate' },
      { Icon: AccessTimeIcon, color: '#06b6d4', label: '24/7 Sales Support' },
    ],
    iconBg: '#f59e0b',
    AgentIcon: ShoppingCartIcon,
    available: false,
    verified: false,
  },
];

// ─── AgentTypeCard ─────────────────────────────────────────────────────────────
function AgentTypeCard({ agentType, onGetStarted, isCreating, anyCreating }) {
  const [favorited, setFavorited] = useState(false);
  const { name, badge, description, stats, iconBg, AgentIcon, available, verified } = agentType;

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${SLATE[200]}`,
        borderRadius: '12px',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        position: 'relative',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          borderColor: SLATE[300],
        },
      }}
    >
      {/* Favorite icon */}
      <IconButton
        size="small"
        onClick={() => setFavorited(f => !f)}
        sx={{ position: 'absolute', top: 12, right: 12, color: favorited ? '#ef4444' : SLATE[300] }}
      >
        {favorited ? (
          <FavoriteIcon sx={{ fontSize: 18 }} />
        ) : (
          <FavoriteBorderIcon sx={{ fontSize: 18 }} />
        )}
      </IconButton>

      {/* Agent icon */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AgentIcon sx={{ color: '#fff', fontSize: 24 }} />
      </Box>

      {/* Name + badge */}
      <Box>
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: SLATE[900], mb: '6px' }}>
          {name}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            px: '8px',
            py: '2px',
            borderRadius: '100px',
            bgcolor: badge.bg,
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: badge.color }} />
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: badge.color }}>
            {badge.label}
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      <Typography sx={{ fontSize: '0.8125rem', color: SLATE[500], lineHeight: 1.55, flex: 1 }}>
        {description}
      </Typography>

      {/* Stats */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {stats.map((stat, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <stat.Icon sx={{ fontSize: 14, color: stat.color }} />
            <Typography sx={{ fontSize: '0.75rem', color: SLATE[600], fontWeight: 500 }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Action buttons */}
      {available ? (
        <Box sx={{ display: 'flex', gap: '8px', mt: '4px' }}>
          <Button
            variant="outlined"
            size="small"
            sx={{
              flex: 1,
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: SLATE[700],
              borderColor: SLATE[300],
              borderRadius: '8px',
              '&:hover': { borderColor: SLATE[400], bgcolor: SLATE[50] },
            }}
          >
            Learn More
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => onGetStarted(agentType)}
            disabled={anyCreating}
            startIcon={isCreating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 500,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: INDIGO[400], color: '#fff' },
            }}
          >
            {isCreating ? 'Creating...' : 'Get Started'}
          </Button>
        </Box>
      ) : (
        <Button
          variant="outlined"
          size="small"
          disabled
          fullWidth
          sx={{
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 500,
            borderRadius: '8px',
            mt: '4px',
          }}
        >
          Coming Soon
        </Button>
      )}

      {/* Verified badge */}
      {verified && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <VerifiedIcon sx={{ fontSize: 14, color: '#10b981' }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 500 }}>
            Verified
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── AgentTypeSelector ─────────────────────────────────────────────────────────
export default function AgentTypeSelector() {
  const currentApp = useSelector(state => state?.apps?.selectedApp);
  const tokens = useSelector(state => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [creatingId, setCreatingId] = useState(null);

  const handleGetStarted = async agentType => {
    setCreatingId(agentType.id);
    try {
      const response = await makeRequest(
        `/v1/apps/${currentApp.id}/agents/create_agent_and_conversation`,
        {
          method: 'POST',
          body: { agent_type: agentType.id },
          authTokens: tokens,
        }
      );
      if (response?.agent) {
        if (response.agent.site_code) dispatch(setSelectedAgentSiteCode(response.agent.site_code));
        dispatch(setSelectedAgentName(response.agent.name));
        dispatch(setSelectedAgentMode(response.agent.agent_mode || null));
        navigate(`/${currentApp.url_code}/agent/${response.agent.id}/chat`);
      }
    } catch (_err) {
      toast.error('Failed to create agent. Please try again.');
    } finally {
      setCreatingId(null);
    }
  };

  const handleCancel = () => {
    navigate(`/${currentApp?.url_code}/`);
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: SLATE[50],
        px: { xs: 3, md: 6 },
        py: 5,
      }}
    >
      {/* Header */}
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 5 }}
      >
        <Box>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: SLATE[900], mb: '6px' }}>
            Choose the Type of AI Agent You're Building
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: SLATE[500] }}>
            Select an agent type to accelerate your development
          </Typography>
        </Box>
        <Button
          startIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
          onClick={handleCancel}
          sx={{
            textTransform: 'none',
            fontSize: '0.875rem',
            color: SLATE[600],
            fontWeight: 500,
            '&:hover': { bgcolor: SLATE[100] },
          }}
        >
          Cancel
        </Button>
      </Box>

      {/* Legacy agent option — hidden for agent-mode clients */}
      {currentApp?.client_mode !== 'agent' && (
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            bgcolor: '#fff',
            border: `1px solid ${SLATE[200]}`,
            borderRadius: '12px',
            px: 3,
            py: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '10px',
                bgcolor: SLATE[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <BuildOutlinedIcon sx={{ fontSize: 22, color: SLATE[600] }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: SLATE[900] }}>
                Build a Legacy Agent
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: SLATE[500], mt: '2px' }}>
                Skip the AI builder and configure your agent manually — full control, no guided
                setup.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<BuildOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={() => navigate(`/${currentApp?.url_code}/agents/new?mode=legacy`)}
            disabled={creatingId !== null}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: SLATE[700],
              borderColor: SLATE[300],
              borderRadius: '8px',
              px: 2.5,
              py: '8px',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: SLATE[500], bgcolor: SLATE[50], color: SLATE[900] },
            }}
          >
            Create Legacy Agent
          </Button>
        </Box>
      )}

      {/* Cards grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '20px',
          '@media (max-width: 1024px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
          '@media (max-width: 640px)': { gridTemplateColumns: 'repeat(1, 1fr)' },
        }}
      >
        {AGENT_TYPES.map(agentType => (
          <AgentTypeCard
            key={agentType.id}
            agentType={agentType}
            onGetStarted={handleGetStarted}
            isCreating={creatingId === agentType.id}
            anyCreating={creatingId !== null}
          />
        ))}
      </Box>
    </Box>
  );
}
